"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Check, Shield, CreditCard, User, FileText, Fuel, CircleAlert, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function RentalTermsFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const requirements = [{
    icon: <FileText className="w-5 h-5" />,
    title: "Valid Driver's License",
    description: "Must be held for at least 1 year. International permits required for non-Latin scripts."
  }, {
    icon: <User className="w-5 h-5" />,
    title: "Minimum Age Requirement",
    description: "Drivers must be 21+. A young driver surcharge applies for ages 21-24."
  }, {
    icon: <CreditCard className="w-5 h-5" />,
    title: "Security Deposit",
    description: "A valid credit card in the main driver's name is required for the pre-authorization hold."
  }, {
    icon: <Shield className="w-5 h-5" />,
    title: "Identity Verification",
    description: "Valid passport or national ID card matching the driver's license is mandatory."
  }];
  const faqs = [{
    question: "What does the insurance coverage include?",
    answer: "Our standard rental includes Third Party Liability and Collision Damage Waiver (CDW) with a deductible. You can upgrade to Super Cover for zero excess and complete peace of mind during your rental period."
  }, {
    question: "What is the fuel policy?",
    answer: "We operate on a 'Full-to-Full' policy. You will receive the vehicle with a full tank and must return it full to avoid refueling charges. Electric vehicles should be returned with at least 80% charge."
  }, {
    question: "Can I take the rental vehicle across borders?",
    answer: "Cross-border travel is permitted to select countries within the EU. Prior authorization is required, and an additional cross-border fee may apply to cover extended insurance and roadside assistance."
  }, {
    question: "What happens if I return the car late?",
    answer: "We offer a 59-minute grace period. Returns later than this will incur an additional day's rental charge at the current daily rate. Please contact us if you anticipate a delay."
  }, {
    question: "Is there a mileage limit on rentals?",
    answer: "Most of our short-term rentals come with unlimited mileage. For long-term leases or specific high-performance models, a daily limit may apply, with a per-kilometer fee for excess usage."
  }];
  return <section id="rental-terms-faq" ref={ref} className={cn("dark bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Rental Requirements
              </h2>
              <p className="text-muted-foreground text-lg">
                To ensure a smooth handover, please ensure you meet the following criteria before arriving at our desk.
              </p>
            </div>

            <div className="grid gap-6">
              {requirements.map((req, i) => <div key={i} data-index={i} className="flex gap-4 p-4 rounded-lg border border-border/50 bg-card/50 hover:border-primary/50 transition-colors duration-300">
                  <div className="flex-shrink-0 mt-1 text-primary">
                    {req.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{req.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {req.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="font-semibold">
                <Link to="/rental-terms">
                  Read Full Terms & Conditions
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground text-lg">
                Quick answers to common questions about our fleet, policies, and services.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                  <AccordionTrigger className="text-left text-base md:text-lg font-medium hover:text-primary transition-colors py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="bg-secondary/30 border border-border rounded-lg p-6 mt-8 flex items-start gap-4">
              <CircleAlert className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Still have questions?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Our support team is available 24/7 to assist you with any specific inquiries regarding your rental.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/contact">Contact Support</Link>
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}